import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  DndContext, 
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { 
  getFieldTypesList,
  generateFieldId,
  generateOptionId,
  getNextOptionColor,
  DEFAULT_OPTION_COLORS,
} from '../../config/customFieldTypes';
import { FIELD_DEFINITIONS } from '../../config/fieldVisibility/definitions';
import ConfirmModal from '../../FlowchartView/components/ConfirmModal';
import SortableFieldRow from './components/SortableFieldRow';
import './styles/index.css';

/**
 * CustomFieldsManager Component
 * 
 * Modal for managing custom fields at the campaign level.
 * Users can add, edit, delete, and reorder custom fields via drag-and-drop.
 * 
 * PERFORMANCE: Uses completely local state while open.
 * Only syncs to parent on close - no parent re-renders during editing.
 */
const CustomFieldsManager = ({ 
  isOpen, 
  onClose, 
  customFields: initialCustomFields = [], 
  allTasks = [], // All tasks in campaign (for delete warning)
  onCustomFieldsChange,
}) => {
  // PERFORMANCE: Local state - completely isolated from parent
  const [localFields, setLocalFields] = useState([]);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [newFieldId, setNewFieldId] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { fieldId, fieldName, taskCount }
  const newFieldInputRef = useRef(null);
  const fieldTypes = getFieldTypesList();

  // Build set of reserved field names from built-in field definitions
  // This dynamically reads from FIELD_DEFINITIONS so future additions are automatically included
  const reservedFieldNames = useMemo(() => {
    const names = new Set();
    for (const field of FIELD_DEFINITIONS) {
      // Add the main label (lowercase for case-insensitive comparison)
      if (field.label) {
        names.add(field.label.toLowerCase());
      }
      // Also add the field ID as a reserved name (e.g., "dueDate", "estimatedDurationMinutes")
      if (field.id) {
        names.add(field.id.toLowerCase());
      }
    }
    // Add some additional common names that might conflict
    names.add('name');
    names.add('title');
    names.add('mission');
    names.add('task');
    return names;
  }, []);

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalFields(initialCustomFields);
      setEditingFieldId(null);
      setNewFieldId(null);
      setValidationError(null);
    }
  }, [isOpen, initialCustomFields]);

  // Focus new field input
  useEffect(() => {
    if (newFieldId && newFieldInputRef.current) {
      newFieldInputRef.current.focus();
    }
  }, [newFieldId]);

  // Drag sensors - no activation constraint for instant drag
  const sensors = useSensors(
    useSensor(PointerSensor)
  );

  // Validate fields - check for empty names, duplicates, and reserved names
  const validateFields = useCallback(() => {
    const emptyNameFields = localFields.filter(f => !f.name.trim());
    if (emptyNameFields.length > 0) {
      setValidationError(`Please add a name to ${emptyNameFields.length === 1 ? 'the unnamed field' : `all ${emptyNameFields.length} unnamed fields`} before saving.`);
      return false;
    }
    
    // Check for duplicate names (case-insensitive) and reserved names
    const namesSeen = new Map(); // lowercase name -> original name
    for (const field of localFields) {
      const lowerName = field.name.trim().toLowerCase();
      
      // Check for reserved/built-in field names
      if (reservedFieldNames.has(lowerName)) {
        setValidationError(`"${field.name}" is a reserved field name. Please choose a different name.`);
        return false;
      }
      
      // Check for duplicates among custom fields
      if (namesSeen.has(lowerName)) {
        setValidationError(`Duplicate field name: "${field.name}". Each custom field must have a unique name.`);
        return false;
      }
      namesSeen.set(lowerName, field.name);
    }
    
    return true;
  }, [localFields, reservedFieldNames]);

  // Handle close - validate and sync to parent
  const handleClose = useCallback(() => {
    if (!validateFields()) {
      return; // Don't close if validation fails
    }
    onCustomFieldsChange?.(localFields);
    onClose();
  }, [localFields, onCustomFieldsChange, onClose, validateFields]);

  // Handle cancel - close without saving (discard changes)
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // All handlers work on local state only - no parent updates
  const handleCreateField = useCallback(() => {
    const id = generateFieldId();
    setLocalFields(prev => {
      const shifted = prev.map(f => ({ ...f, order: f.order + 1 }));
      return [{ id, name: '', type: 'text', order: 0, required: false }, ...shifted];
    });
    setNewFieldId(id);
  }, []);

  const handleFieldNameCommit = useCallback((fieldId, name) => {
    // Just update the name - don't delete if empty
    setLocalFields(prev => 
      prev.map(f => f.id === fieldId ? { ...f, name } : f)
    );
    setNewFieldId(null);
    // Clear validation error when user edits a field
    setValidationError(null);
  }, []);

  const handleFieldNameKeyDown = useCallback((e, fieldId, name) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setNewFieldId(null);
      e.target.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // On Escape, revert to previous name (don't save empty)
      setNewFieldId(null);
      e.target.blur();
    }
  }, []);

  const handleFieldTypeChange = useCallback((fieldId, newType) => {
    setLocalFields(prev => prev.map(field => {
      if (field.id !== fieldId) return field;
      const updates = { type: newType };
      if (newType === 'select' && !field.options) {
        updates.options = [{ id: generateOptionId(), label: 'Option 1', color: DEFAULT_OPTION_COLORS[0] }];
      }
      return { ...field, ...updates };
    }));
  }, []);

  // Check if any tasks have data for a field
  const getTaskCountWithFieldData = useCallback((fieldId) => {
    return allTasks.filter(task => {
      const value = task.customFieldValues?.[fieldId];
      // Check if the field has a non-empty value
      return value !== undefined && value !== null && value !== '';
    }).length;
  }, [allTasks]);

  // Request to delete a field - shows warning if tasks have data
  const handleRequestDeleteField = useCallback((fieldId) => {
    const field = localFields.find(f => f.id === fieldId);
    const fieldName = field?.name || 'this field';
    const taskCount = getTaskCountWithFieldData(fieldId);
    
    if (taskCount > 0) {
      // Show confirmation modal with warning
      setDeleteConfirm({ fieldId, fieldName, taskCount });
    } else {
      // No data, delete immediately
      setLocalFields(prev => 
        prev.filter(f => f.id !== fieldId).map((f, i) => ({ ...f, order: i }))
      );
    }
  }, [localFields, getTaskCountWithFieldData]);

  // Confirm delete after warning
  const handleConfirmDeleteField = useCallback(() => {
    if (deleteConfirm) {
      setLocalFields(prev => 
        prev.filter(f => f.id !== deleteConfirm.fieldId).map((f, i) => ({ ...f, order: i }))
      );
      setDeleteConfirm(null);
    }
  }, [deleteConfirm]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    setLocalFields(prev => {
      const oldIndex = prev.findIndex(f => f.id === active.id);
      const newIndex = prev.findIndex(f => f.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      
      const reordered = [...prev];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);
      return reordered.map((f, i) => ({ ...f, order: i }));
    });
  }, []);

  const handleAddOption = useCallback((fieldId) => {
    setLocalFields(prev => prev.map(field => {
      if (field.id !== fieldId) return field;
      const newOption = {
        id: generateOptionId(),
        label: `Option ${(field.options?.length || 0) + 1}`,
        color: getNextOptionColor(field.options || []),
      };
      return { ...field, options: [...(field.options || []), newOption] };
    }));
  }, []);

  const handleUpdateOption = useCallback((fieldId, optionId, updates) => {
    setLocalFields(prev => prev.map(field => {
      if (field.id !== fieldId) return field;
      return {
        ...field,
        options: field.options?.map(opt => opt.id === optionId ? { ...opt, ...updates } : opt),
      };
    }));
  }, []);

  const handleDeleteOption = useCallback((fieldId, optionId) => {
    setLocalFields(prev => prev.map(field => {
      if (field.id !== fieldId) return field;
      return { ...field, options: field.options?.filter(opt => opt.id !== optionId) };
    }));
  }, []);

  // Close on escape (cancel without saving)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !newFieldId) {
        handleCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, newFieldId, handleCancel]);

  if (!isOpen) return null;

  const sortedFields = [...localFields].sort((a, b) => a.order - b.order);

  const modalContent = (
    <div className="custom-fields-manager__overlay" onClick={handleCancel}>
      <div className="custom-fields-manager" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="custom-fields-manager__header">
          <h2 className="custom-fields-manager__title">Custom Fields</h2>
          <button className="custom-fields-manager__close" onClick={handleCancel}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="custom-fields-manager__content">
          {/* Add button at TOP */}
          <button
            className="custom-fields-manager__add-btn"
            onClick={handleCreateField}
          >
            <span className="custom-fields-manager__add-icon">+</span>
            Create New Custom Field
          </button>

          {/* Fields list with drag-and-drop */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedFields.map(f => f.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="custom-fields-manager__list">
                {sortedFields.map((field, index) => (
                  <SortableFieldRow
                    key={field.id}
                    field={field}
                    index={index}
                    totalFields={sortedFields.length}
                    fieldTypes={fieldTypes}
                    isNew={field.id === newFieldId}
                    newFieldInputRef={newFieldInputRef}
                    onFieldNameCommit={handleFieldNameCommit}
                    onFieldNameKeyDown={handleFieldNameKeyDown}
                    onFieldTypeChange={handleFieldTypeChange}
                    onDeleteField={handleRequestDeleteField}
                    onToggleExpanded={(fieldId) => setEditingFieldId(editingFieldId === fieldId ? null : fieldId)}
                    isExpanded={editingFieldId === field.id}
                    onAddOption={handleAddOption}
                    onUpdateOption={handleUpdateOption}
                    onDeleteOption={handleDeleteOption}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Empty state */}
          {localFields.length === 0 && (
            <div className="custom-fields-manager__empty">
              <p>No custom fields yet.</p>
              <p className="custom-fields-manager__empty-hint">
                Add fields to track additional information on your missions.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="custom-fields-manager__footer">
          {validationError && (
            <div className="custom-fields-manager__error">
              {validationError}
            </div>
          )}
          <button className="custom-fields-manager__done-btn" onClick={handleClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      
      {/* Delete confirmation modal for fields with data */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        title="Delete Field with Data"
        message={
          `⚠️ ${deleteConfirm?.taskCount} mission${deleteConfirm?.taskCount === 1 ? ' has' : 's have'} data in the "${deleteConfirm?.fieldName}" field.\n\nIf you delete this field, all that data will be permanently lost.`
        }
        confirmLabel="Delete Field"
        cancelLabel="Cancel"
        confirmVariant="warning"
        onConfirm={handleConfirmDeleteField}
        onCancel={() => setDeleteConfirm(null)}
      />
    </>
  );
};

export default CustomFieldsManager;
